// Self-serve signup with a 14-day trial, and the signed-in billing endpoints. Flow + rules:
// docs/TRIALS.md. Import as `@gf/shared/signup`.
//
// ── Signup (/api/auth/signup, public, JSON; errors { error, code }) ─────────
// POST /start     { plan, name, email, code?, website? } → { ok }   emails a link to /signup#token=…
//                                                    (code: a discount code; 400 promo_invalid when it can't be used)
// POST /check-code { code }                        → SignupDiscount  (400 promo_invalid; rate-limited)
// POST /promo     { session, code: string | null } → SignupState   add, change or remove the discount code
// POST /recover   { email }                        → { ok }   a new email link for a pending signup
// POST /email     { token }                        → SignupState & { session }
// POST /status    { session }                      → SignupState
// POST /password  { session, password }            → SignupState
// POST /plan      { session, plan }                → SignupState   change the plan before the card step
// POST /card      { session }                      → SignupCard    the card-check PaymentIntent (manual capture,
//                                                                    the plan's monthly price, saved for the trial)
// POST /activate  { session }                      → { ok, url }   after Stripe.js confirms the card: checks the
//                                                                    hold, releases it, starts the trial, signs in
// Codes: email_taken (409), card_not_confirmed (402: the hold didn't go through, try another card),
// trial_card_used (409: this card already had a free trial), signup_session_expired (401),
// promo_invalid (400 on /start, /check-code, /promo; 409 on /activate when the code was used up meanwhile).
// A pending signup can also sign in at /login with its password; it lands back on /signup.
//
// ── Billing (/api/billing, session) ─────────────────────────────────────────
// GET  /summary → BillingSummary
// POST /portal  → { url }   Stripe's billing portal: change plan, update the card, cancel.

import type { PlanTier } from "./plans";

export type SignupStage = "password" | "card";

/** A discount code (a Stripe promotion code on a GramFather coupon) applied to the trial subscription. */
export interface SignupDiscount {
  code: string;
  /** "25% off for life", "$10 off your first month", "20% off for 3 months". */
  label: string;
  percentOff: number | null;
  /** Fixed discount in cents (when not a percentage). */
  amountOffCents: number | null;
  duration: "once" | "repeating" | "forever";
  /** repeating: how many monthly bills it covers. */
  months: number | null;
}

/** "25% off for life", "$10 off your first month", "20% off for 3 months". */
export function discountLabel(d: Pick<SignupDiscount, "percentOff" | "amountOffCents" | "duration" | "months">): string {
  const cents = d.amountOffCents ?? 0;
  const off = d.percentOff != null ? `${+d.percentOff.toFixed(2)}% off` : `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)} off`;
  if (d.duration === "forever") return `${off} for life`;
  if (d.duration === "repeating" && d.months && d.months > 1) return `${off} for ${d.months} months`;
  return `${off} your first month`;
}

/** The monthly price after the discount, in cents (Stripe rounds a percentage discount to the nearest cent). */
export function discountedCents(priceCents: number, d: Pick<SignupDiscount, "percentOff" | "amountOffCents"> | null | undefined): number {
  if (!d) return priceCents;
  const off = d.percentOff != null ? Math.round((priceCents * d.percentOff) / 100) : (d.amountOffCents ?? 0);
  return Math.max(0, priceCents - off);
}

export interface SignupState {
  email: string;
  name: string;
  plan: PlanTier;
  stage: SignupStage;
  /** The discount code this signup will use, or null. */
  discount: SignupDiscount | null;
}

export interface SignupCard {
  clientSecret: string;
  publishableKey: string;
  plan: PlanTier;
  /** The hold placed on the card (the plan's first month), in cents. Released right away. */
  amountCents: number;
  trialDays: number;
  /** When the first charge happens if they don't cancel (epoch ms, from now). */
  trialEndsAt: number;
}

export interface BillingSubscription {
  /** Standard tier, or null for the Reseller Program. */
  plan: PlanTier | null;
  reseller: boolean;
  /** Stripe status: trialing | active | past_due | canceled | … */
  status: string;
  trialEndsAt: number | null;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  /** Price per billing interval in cents. */
  amountCents: number | null;
  interval?: 'month' | 'year';
}

export interface BillingSummary {
  subscription: BillingSubscription | null;
  /** Whether POST /portal can open Stripe's billing portal for this account. */
  manageable: boolean;
}
